import { patternGeometry, type ShapeElement } from "@cva/shared";
import { useMemo } from "react";

/**
 * Pattern over a rectangle's or ellipse's fill (scene/patterns.ts): one
 * vector path, rotated around the centre and clipped to the shape.
 */
export function PatternLayer({
  element,
  id,
  inset,
}: {
  element: ShapeElement;
  id: string;
  inset: number;
}) {
  const pattern = element.pattern;
  const { width: w, height: h } = element;
  const geometry = useMemo(
    () => (pattern ? patternGeometry(pattern, w, h) : null),
    [pattern, w, h],
  );
  if (!pattern || !geometry || (element.shape !== "rect" && element.shape !== "ellipse"))
    return null;
  const iw = Math.max(0, w - inset * 2);
  const ih = Math.max(0, h - inset * 2);
  const clip =
    element.shape === "rect" ? (
      <rect
        x={inset}
        y={inset}
        width={iw}
        height={ih}
        rx={Math.max(0, Math.min(element.radius - inset, iw / 2, ih / 2))}
      />
    ) : (
      <ellipse cx={w / 2} cy={h / 2} rx={iw / 2} ry={ih / 2} />
    );
  const opacity = pattern.opacity < 1 ? pattern.opacity : undefined;
  return (
    <>
      <defs>
        <clipPath id={id}>{clip}</clipPath>
      </defs>
      <g clipPath={`url(#${id})`}>
        <path
          d={geometry.d}
          transform={pattern.rotation ? `rotate(${pattern.rotation} ${w / 2} ${h / 2})` : undefined}
          {...(geometry.mode === "fill"
            ? { fill: pattern.color, fillOpacity: opacity }
            : {
                fill: "none",
                stroke: pattern.color,
                strokeWidth: geometry.strokeWidth,
                strokeOpacity: opacity,
                strokeLinecap: "round" as const,
                strokeLinejoin: "round" as const,
              })}
        />
      </g>
    </>
  );
}
